import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { RegisterSearch } from "../components/RegisterSearch";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { hasAnyPermission, hasPermission } from "../lib/permissions";
import { inr, fmtDate } from "../lib/format";
import { CalendarDays, ChevronLeft, Copy, Edit, Eye, Mail, Phone, Plus, Trash2, Briefcase, Wallet } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { usePopup } from "../components/AppPopups";
import { EmptyState, MetricStrip } from "../components/CommandSystem";
import { PaginationFooter } from "../components/PaginationFooter";

const EMPTY = { company: "", contact_person: "", mobile: "", email: "", notes: "", business_type: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PAGE_SIZE = 24;

const FILTERS = [
  { id: "all",         label: "All" },
  { id: "active",      label: "Active" },
  { id: "past",        label: "Past" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function RequiredLabel({ children }) {
  return (
    <Label className="text-xs font-semibold text-slate-600">
      {children} <span className="text-blue-500">*</span>
    </Label>
  );
}

function onlyLetters(value) {
  return value.replace(/[^A-Za-z\s]/g, "").replace(/\s{2,}/g, " ").slice(0, 30);
}

function onlyDigits(value, limit = 10) {
  return value.replace(/\D/g, "").slice(0, limit);
}

function NotesCounter({ value, limit = 50 }) {
  const used = (value || "").length;
  const pct = (used / limit) * 100;
  const hue = Math.round(120 - pct * 1.2);
  const color = `hsl(${Math.max(0, hue)} 76% 45%)`;
  return (
    <div
      style={{ background: `conic-gradient(${color} ${pct}%, #e2e8f0 ${pct}% 100%)` }}
      className="flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-bold flex-shrink-0"
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-slate-700">
        {limit - used}
      </span>
    </div>
  );
}

function ContextMenuItem({ icon: Icon, label, danger = false, disabled = false, title, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      onClick={(event) => {
        event.stopPropagation();
        if (!disabled) onClick?.();
      }}
      className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${danger ? "text-rose-700 hover:bg-rose-50" : "text-slate-700 hover:bg-slate-50"}`}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function Clients() {
  const { user } = useAuth();
  const nav = useNavigate();
  const popup = usePopup();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingClients, setLoadingClients] = useState(false);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [q, setQ] = useState("");
  const [committedQ, setCommittedQ] = useState("");
  const [businessTypeSuggestions, setBusinessTypeSuggestions] = useState([]);
  const [businessTypeFocused, setBusinessTypeFocused] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const canCreate = hasPermission(user, "create_clients");
  const canViewContact = hasPermission(user, "view_client_contact_details");
  const canViewRevenue = hasAnyPermission(user, ["view_basic_revenue", "view_campaign_amounts", "view_profit_and_loss", "view_financial_reports"]);
  const canDelete = hasPermission(user, "delete_clients");
  const canEditAnyClient = hasAnyPermission(user, ["edit_assigned_clients", "edit_all_clients", "modify_records"]);
  const clientCacheRef = useRef(new Map());

  const cacheKeyFor = useCallback((pageNum) => `${activeFilter}|${committedQ.trim().toLowerCase()}|${pageNum}`, [activeFilter, committedQ]);

  const clearClientCache = useCallback(() => {
    clientCacheRef.current.clear();
  }, []);

  const load = useCallback(async (pageNum, options = {}) => {
    const cacheKey = cacheKeyFor(pageNum);
    if (!options.force) {
      const cached = clientCacheRef.current.get(cacheKey);
      if (cached) {
        setItems(cached.items);
        setTotal(cached.total);
        setPage(pageNum);
        return;
      }
    }
      setLoadingClients(true);
    try {
      const offset = (pageNum - 1) * PAGE_SIZE;
      const params = { limit: PAGE_SIZE, offset };
      if (committedQ) params.q = committedQ;
      if (activeFilter !== "all") params.status = activeFilter;
      const { data } = await api.get("/clients", { params, skipGlobalLoader: true });
      if (data && !Array.isArray(data) && data.clients !== undefined) {
        const nextItems = data.clients ?? [];
        const nextTotal = data.total ?? 0;
        setItems(nextItems);
        setTotal(nextTotal);
        clientCacheRef.current.set(cacheKey, { items: nextItems, total: nextTotal });
      } else {
        const nextItems = Array.isArray(data) ? data : [];
        setItems(nextItems);
        setTotal(0);
        clientCacheRef.current.set(cacheKey, { items: nextItems, total: 0 });
      }
      setPage(pageNum);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  }, [activeFilter, cacheKeyFor, committedQ]);

  // Reload from page 1 whenever filters or committed search change.
  useEffect(() => { load(1); }, [load]);

  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = () => setContextMenu(null);
    const onKeyDown = (event) => { if (event.key === "Escape") close(); };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [contextMenu]);

  async function loadBusinessTypes() {
    try {
      const { data } = await api.get("/clients/business-types", { skipGlobalLoader: true });
      setBusinessTypeSuggestions(Array.isArray(data) ? data : []);
    } catch {
      setBusinessTypeSuggestions([]);
    }
  }
  useEffect(() => { loadBusinessTypes(); }, []);

  function commitSearch() {
    const nextQuery = q.trim();
    if (nextQuery !== committedQ) {
      clearClientCache();
      setCommittedQ(nextQuery);
    }
    setPage(1);
  }

  function startCreate() { setEditing(null); setForm(EMPTY); setOpen(true); }

  function canEditClient(client) {
    return canEditAnyClient || (hasPermission(user, "edit_own_clients") && client.created_by === user.id);
  }

  function canDeleteClient(client) {
    return canDelete && client?.can_delete !== false && Number(client?.campaign_count || 0) === 0 && Number(client?.proposal_count || 0) === 0;
  }

  function deleteDisabledTitle(client) {
    if (canDeleteClient(client)) return "Delete client";
    return `Cannot delete: linked to ${Number(client?.campaign_count || 0)} campaign(s) and ${Number(client?.proposal_count || 0)} proposal(s).`;
  }

  function startEdit(client) {
    setEditing(client);
    setForm({ ...EMPTY, ...client });
    setOpen(true);
    setContextMenu(null);
  }

  function openTileMenu(event, client) {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      x: Math.min(event.clientX, window.innerWidth - 240),
      y: Math.min(event.clientY, window.innerHeight - 220),
      client,
    });
  }

  async function copyClientInfo(client) {
    const parts = [
      client.company,
      client.contact_person,
      canViewContact ? client.mobile : "",
      canViewContact ? client.email : "",
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(parts.join(" | "));
      toast.success("Client details copied");
    } catch {
      toast.error("Client details could not be copied");
    } finally {
      setContextMenu(null);
    }
  }

  async function deleteClient(client) {
    if (!canDeleteClient(client)) return;
    setContextMenu(null);
    const ok = await popup.confirm({
      title: `Delete ${client.company}?`,
      description: "This client will be removed from active records.",
      confirmText: "Delete Client",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/clients/${client.id}`);
      toast.success("Client deleted");
      clearClientCache();
      load(page, { force: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Client could not be deleted");
    }
  }

  async function save() {
    if (!form.company.trim()) { toast.error("Company name is required"); return; }
    if (!form.contact_person.trim()) { toast.error("Contact person is required"); return; }
    if (!form.mobile) { toast.error("Mobile number is required"); return; }
    if (form.mobile.length < 10) { toast.error("Mobile must be exactly 10 digits"); return; }
    if (!form.email) { toast.error("Email is required"); return; }
    if (!EMAIL_RE.test(form.email)) { toast.error("Please enter a valid email address"); return; }
    if (!form.business_type.trim()) { toast.error("Business type is required"); return; }

    try {
      const payload = { ...form, business_type: form.business_type.trim() };
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Client updated" : "Client added");
      setOpen(false);
      clearClientCache();
      load(editing ? page : 1, { force: true });
      loadBusinessTypes();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  const clients = Array.isArray(items) ? items : [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const matchingBusinessTypes = useMemo(() => {
    const query = form.business_type.trim().toLowerCase();
    if (!query) return businessTypeSuggestions.slice(0, 6);
    return businessTypeSuggestions
      .filter((type) => type.toLowerCase().startsWith(query))
      .slice(0, 6);
  }, [businessTypeSuggestions, form.business_type]);

  return (
    <Layout
      title="Clients"
      subtitle="Client Directory"
      actions={
        canCreate ? (
          <button
            onClick={startCreate}
            data-testid="add-client-btn"
            className="hx-primary-action"
          >
            <Plus size={15} /> Add Client
          </button>
        ) : null
      }
    >
      <div className="hx-hoardings-page hx-clients-page">
      <section className="hx-inventory-register" aria-busy={loadingClients}>
        <header>
          <div>
            <h2>Client Register</h2>
            <p>{total || clients.length} records in current view</p>
          </div>
          <div className="hx-register-controls">
            <RegisterSearch
              value={q}
              onChange={setQ}
              onSearch={commitSearch}
              placeholder="Search company or contact..."
              testId="clients-search"
              ariaLabel="Search clients"
            />
            {loadingClients && (
              <span className="hx-register-refresh" role="status">
                <span aria-hidden="true" />
                Updating
              </span>
            )}
          </div>
        </header>

        <div className="hx-register-filterbar">
          {FILTERS.map(f => {
            const active = activeFilter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => { setActiveFilter(f.id); }}
                className={`hx-client-filter-chip ${active ? "is-active" : ""}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

      <div className="hx-clients-grid" data-testid="clients-grid">
        {clients.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => nav(`/clients/${c.id}`)}
            onContextMenu={(event) => openTileMenu(event, c)}
            className="hx-client-tile w-full text-left"
          >
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <div className="hx-client-title">{c.company}</div>
                {c.business_type && (
                  <div className="hx-client-type">
                    <Briefcase size={9} />
                    {c.business_type}
                  </div>
                )}
              </div>
              <div className="hx-client-contact-line">
                <span>{c.contact_person || "No contact person"}</span>
                {canViewContact && c.mobile && (
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <Phone size={12} /> <span className="font-mono">{c.mobile}</span>
                  </span>
                )}
                {canViewContact && c.email && (
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <Mail size={12} /> <span className="truncate">{c.email}</span>
                  </span>
                )}
              </div>
            </div>

            <div className="hx-client-stats">
              <Stat label="Active hoardings" value={c.active_hoardings ?? 0} />
              {canViewRevenue && <Stat label="Total spend" value={inr(c.total_spend ?? 0)} />}
            </div>

            <div className="hx-client-date">
              <span>Onboarded</span>
              <strong>{fmtDate(c.created_at)}</strong>
            </div>

          </button>
        ))}
        {clients.length === 0 && (
          <div className="col-span-full">
            <EmptyState
              title={total === 0 && !committedQ && activeFilter === "all" ? "No clients yet" : "No clients match this filter"}
              description={total === 0 && !committedQ && activeFilter === "all" ? "Add your first client to start tracking campaigns and revenue." : "Try a different search term or filter."}
            />
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <PaginationFooter page={page} pageSize={PAGE_SIZE} total={total} onPageChange={load} label="clients" />
      )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px] p-4">
          <DialogHeader className="space-y-1">
            <DialogTitle>{editing ? "Edit Client" : "Add Client"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2.5 py-1">
            {/* Company Name */}
            <div className="sm:col-span-2 space-y-1">
              <RequiredLabel>Company Name</RequiredLabel>
              <Input
                data-testid="client-company"
                value={form.company}
                maxLength={30}
                onChange={e => setForm({ ...form, company: e.target.value.slice(0, 30) })}
                placeholder="e.g. Coca-Cola India"
              />
              <div className="-mt-0.5 text-right text-[10px] text-slate-400">{form.company.length}/30</div>
            </div>

            {/* Contact Person */}
            <div className="space-y-1">
              <RequiredLabel>Contact Person</RequiredLabel>
              <Input
                value={form.contact_person}
                maxLength={30}
                onChange={e => setForm({ ...form, contact_person: onlyLetters(e.target.value) })}
                placeholder="Full name"
              />
              <div className="-mt-0.5 text-right text-[10px] text-slate-400">{form.contact_person.length}/30</div>
            </div>

            {/* Mobile */}
            <div className="space-y-1">
              <RequiredLabel>Mobile</RequiredLabel>
              <Input
                value={form.mobile}
                inputMode="numeric"
                maxLength={10}
                onChange={e => setForm({ ...form, mobile: onlyDigits(e.target.value) })}
                placeholder="10-digit number"
              />
              <div className="-mt-0.5 text-right text-[10px] text-slate-400">{form.mobile.length}/10</div>
            </div>

            {/* Email */}
            <div className="sm:col-span-2 space-y-1">
              <RequiredLabel>Email</RequiredLabel>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="contact@company.com"
              />
            </div>

            {/* Business Type */}
            <div className="relative sm:col-span-2 space-y-1">
              <RequiredLabel>Business Type</RequiredLabel>
              <Input
                required
                value={form.business_type}
                maxLength={40}
                onFocus={() => setBusinessTypeFocused(true)}
                onBlur={() => window.setTimeout(() => setBusinessTypeFocused(false), 120)}
                onChange={e => setForm({ ...form, business_type: e.target.value.slice(0, 40) })}
                placeholder="e.g. Jewellery, Automobile, Food, Real Estate…"
                autoComplete="off"
              />
              {businessTypeFocused && matchingBusinessTypes.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                  {matchingBusinessTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setForm({ ...form, business_type: type });
                        setBusinessTypeFocused(false);
                      }}
                      className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700"
                    >
                      {type}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="sm:col-span-2 space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Notes</Label>
              <div className="flex items-end gap-2">
                <Textarea
                  rows={2}
                  value={form.notes}
                  maxLength={50}
                  onChange={e => setForm({ ...form, notes: e.target.value.slice(0, 50) })}
                  placeholder="Optional notes about this client…"
                  className="flex-1 resize-none"
                />
                <NotesCounter value={form.notes} limit={50} />
              </div>
              <div className="w-full h-1 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${(form.notes.length / 50) * 100}%`,
                    background: `hsl(${Math.max(0, Math.round(120 - (form.notes.length / 50) * 120))} 76% 45%)`,
                  }}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-1">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="client-save">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {contextMenu && (
        <div
          className="fixed z-[2100] w-56 rounded-lg border border-slate-200 bg-white p-1.5 shadow-xl shadow-slate-950/15"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(event) => event.stopPropagation()}
          onMouseLeave={() => setContextMenu(null)}
        >
          <div className="border-b border-slate-100 px-3 py-2">
            <div className="truncate text-xs font-bold text-slate-900">{contextMenu.client.company}</div>
            <div className="truncate text-[11px] text-slate-500">{contextMenu.client.contact_person || "No contact person"}</div>
          </div>
          <div className="mt-1 space-y-0.5">
            <ContextMenuItem icon={Eye} label="View Details" onClick={() => { setContextMenu(null); nav(`/clients/${contextMenu.client.id}`); }} />
            {canEditClient(contextMenu.client) && (
              <ContextMenuItem icon={Edit} label="Edit Client" onClick={() => startEdit(contextMenu.client)} />
            )}
            <ContextMenuItem icon={Copy} label="Copy Client Info" onClick={() => copyClientInfo(contextMenu.client)} />
            {canDelete && (
              <ContextMenuItem
                icon={Trash2}
                label="Delete Client"
                danger
                disabled={!canDeleteClient(contextMenu.client)}
                title={deleteDisabledTitle(contextMenu.client)}
                onClick={() => deleteClient(contextMenu.client)}
              />
            )}
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
}

export function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const popup = usePopup();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [businessTypeSuggestions, setBusinessTypeSuggestions] = useState([]);
  const [businessTypeFocused, setBusinessTypeFocused] = useState(false);

  const canDelete = hasPermission(user, "delete_clients");
  const canViewContact = hasPermission(user, "view_client_contact_details");
  const canViewRevenue = hasAnyPermission(user, ["view_basic_revenue", "view_campaign_amounts", "view_profit_and_loss", "view_financial_reports"]);
  const canEdit = client && (
    hasAnyPermission(user, ["edit_assigned_clients", "edit_all_clients", "modify_records"]) ||
    (hasPermission(user, "edit_own_clients") && client.created_by === user.id)
  );
  const clientHasUsage = Number(client?.campaign_count || 0) > 0 || Number(client?.proposal_count || 0) > 0 || client?.can_delete === false;
  const canDeleteClient = canDelete && client && !clientHasUsage;
  const deleteDisabledTitle = clientHasUsage
    ? `Cannot delete: linked to ${Number(client?.campaign_count || 0)} campaign(s) and ${Number(client?.proposal_count || 0)} proposal(s).`
    : "Delete client";

  const load = useCallback(() => {
    setLoading(true);
    async function loadClientDetail() {
      const clientReq = api.get(`/clients/${id}`, { skipGlobalLoader: true });
      const collected = [];
      let offset = 0;
      let total = 0;
      do {
        const { data } = await api.get("/campaigns", { params: { client_id: id, limit: 100, offset }, skipGlobalLoader: true });
        const page = Array.isArray(data?.campaigns) ? data.campaigns : [];
        total = Number(data?.total ?? page.length);
        collected.push(...page);
        offset += page.length;
      } while (offset < total && offset > 0);
      const clientRes = await clientReq;
      return { client: clientRes.data || null, campaigns: collected };
    }
    loadClientDetail()
      .then(({ client: nextClient, campaigns: nextCampaigns }) => {
        setClient(nextClient);
        setCampaigns(nextCampaigns.sort((a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0)));
      })
      .catch((err) => {
        toast.error(formatApiError(err.response?.data?.detail) || "Client could not be loaded");
        setClient(null);
        setCampaigns([]);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/clients/business-types", { skipGlobalLoader: true })
      .then(({ data }) => setBusinessTypeSuggestions(Array.isArray(data) ? data : []))
      .catch(() => setBusinessTypeSuggestions([]));
  }, []);

  const matchingBusinessTypes = useMemo(() => {
    const query = form.business_type.trim().toLowerCase();
    if (!query) return businessTypeSuggestions.slice(0, 6);
    return businessTypeSuggestions.filter((type) => type.toLowerCase().startsWith(query)).slice(0, 6);
  }, [businessTypeSuggestions, form.business_type]);

  const campaignRevenue = campaigns.reduce((sum, campaign) => sum + Number(campaign.revenue || campaign.final_invoice_amount || campaign.confirmed_po_value || campaign.estimated_po_value || 0), 0);
  const activeCampaigns = campaigns.filter((campaign) => campaign.status === "Active" || campaign.status === "Expiring Soon").length;

  function openEdit() {
    if (!client) return;
    setForm({ ...EMPTY, ...client });
    setOpen(true);
  }

  async function save() {
    if (!form.company.trim()) { toast.error("Company name is required"); return; }
    if (!form.contact_person.trim()) { toast.error("Contact person is required"); return; }
    if (!form.mobile) { toast.error("Mobile number is required"); return; }
    if (form.mobile.length < 10) { toast.error("Mobile must be exactly 10 digits"); return; }
    if (!form.email) { toast.error("Email is required"); return; }
    if (!EMAIL_RE.test(form.email)) { toast.error("Please enter a valid email address"); return; }
    if (!form.business_type.trim()) { toast.error("Business type is required"); return; }
    try {
      await api.put(`/clients/${id}`, { ...form, business_type: form.business_type.trim() });
      toast.success("Client updated");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Client could not be updated");
    }
  }

  async function remove() {
    if (!client) return;
    const ok = await popup.confirm({
      title: `Delete ${client.company}?`,
      description: "This client will be removed from active records.",
      confirmText: "Delete Client",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted");
      nav("/clients");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Client could not be deleted");
    }
  }

  const actions = (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={() => nav("/clients")}>
        <ChevronLeft size={15} /> Back
      </Button>
      {canEdit && (
        <Button variant="outline" onClick={openEdit}>
          <Edit size={15} /> Edit
        </Button>
      )}
      {canDelete && client && (
        <Button variant="destructive" disabled={!canDeleteClient} onClick={remove} title={deleteDisabledTitle}>
          <Trash2 size={15} /> Delete
        </Button>
      )}
    </div>
  );

  return (
    <Layout title={client?.company || "Client detail"} subtitle="Client Directory" actions={actions}>
      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm font-semibold text-slate-500">Loading client...</div>
      ) : !client ? (
        <EmptyState title="Client not found" description="The client could not be loaded or is outside your access scope." />
      ) : (
        <div className="hx-hoardings-page hx-client-detail-page">
          <MetricStrip
            metrics={[
              { label: "Campaigns", value: campaigns.length, icon: CalendarDays, tone: "indigo" },
              { label: "Active", value: activeCampaigns, icon: Briefcase, tone: "emerald" },
              canViewRevenue && { label: "Revenue", value: inr(campaignRevenue), icon: Wallet, tone: "slate" },
              { label: "Onboarded", value: fmtDate(client.created_at), icon: CalendarDays, tone: "amber" },
            ].filter(Boolean)}
          />

          <section className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
            <section className="rounded-lg border border-slate-300 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-black text-slate-950">Client summary</h2>
              <div className="mt-3 space-y-2 text-sm">
                <DetailLine label="Company" value={client.company} />
                <DetailLine label="Contact" value={client.contact_person} />
                {canViewContact && <DetailLine label="Mobile" value={client.mobile} />}
                {canViewContact && <DetailLine label="Email" value={client.email} />}
                <DetailLine label="Business" value={client.business_type} />
                <DetailLine label="Notes" value={client.notes} />
              </div>
            </section>

            <section className="hx-inventory-register">
              <header>
                <div>
                  <h2>Campaigns</h2>
                  <p>Latest to oldest for this client</p>
                </div>
              </header>
              <div className="hx-inventory-table-wrap hx-campaign-register-table">
                <table>
                  <thead>
                    <tr>
                      <th className="is-id">ID</th>
                      <th className="is-hoarding">Campaign</th>
                      <th className="is-city">City</th>
                      <th className="is-date">Start</th>
                      <th className="is-date">End</th>
                      <th className="is-status">Status</th>
                      {canViewRevenue && <th className="is-revenue">Revenue</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id} onClick={() => nav(`/campaigns/${campaign.id}`)}>
                        <td className="font-mono text-[11px] font-semibold text-slate-500">{campaign.campaign_id}</td>
                        <td>
                          <button type="button" onClick={(event) => { event.stopPropagation(); nav(`/campaigns/${campaign.id}`); }} className="hx-campaign-title-link">
                            {campaign.name}
                          </button>
                        </td>
                        <td className="truncate font-medium text-slate-700">{campaign.city || "-"}</td>
                        <td className="hx-date-cell">{fmtDate(campaign.start_date)}</td>
                        <td className="hx-date-cell">{campaign.is_open_ended ? "Open ended" : fmtDate(campaign.end_date)}</td>
                        <td><span className="inline-flex items-center rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-600">{campaign.status || "-"}</span></td>
                        {canViewRevenue && <td className="hx-price-cell">{inr(campaign.revenue || campaign.final_invoice_amount || campaign.confirmed_po_value || campaign.estimated_po_value || 0)}</td>}
                      </tr>
                    ))}
                    {campaigns.length === 0 && (
                      <tr className="hx-empty-table-row">
                        <td colSpan={canViewRevenue ? 7 : 6} className="py-10 text-center text-slate-500">
                          <EmptyState title="No campaigns for this client" description="Campaigns created for this client will appear here." />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </section>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px] p-4">
          <DialogHeader className="space-y-1">
            <DialogTitle>Edit Client</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2.5 py-1">
            <div className="sm:col-span-2 space-y-1">
              <RequiredLabel>Company Name</RequiredLabel>
              <Input value={form.company} maxLength={30} onChange={(e) => setForm({ ...form, company: e.target.value.slice(0, 30) })} />
            </div>
            <div className="space-y-1">
              <RequiredLabel>Contact Person</RequiredLabel>
              <Input value={form.contact_person} maxLength={30} onChange={(e) => setForm({ ...form, contact_person: onlyLetters(e.target.value) })} />
            </div>
            <div className="space-y-1">
              <RequiredLabel>Mobile</RequiredLabel>
              <Input value={form.mobile} inputMode="numeric" maxLength={10} onChange={(e) => setForm({ ...form, mobile: onlyDigits(e.target.value) })} />
            </div>
            <div className="sm:col-span-2 space-y-1">
              <RequiredLabel>Email</RequiredLabel>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="relative sm:col-span-2 space-y-1">
              <RequiredLabel>Business Type</RequiredLabel>
              <Input value={form.business_type} maxLength={40} onFocus={() => setBusinessTypeFocused(true)} onBlur={() => window.setTimeout(() => setBusinessTypeFocused(false), 120)} onChange={(e) => setForm({ ...form, business_type: e.target.value.slice(0, 40) })} autoComplete="off" />
              {businessTypeFocused && matchingBusinessTypes.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                  {matchingBusinessTypes.map((type) => (
                    <button key={type} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setForm({ ...form, business_type: type }); setBusinessTypeFocused(false); }} className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700">
                      {type}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="sm:col-span-2 space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Notes</Label>
              <Textarea rows={2} value={form.notes} maxLength={50} onChange={(e) => setForm({ ...form, notes: e.target.value.slice(0, 50) })} className="resize-none" />
            </div>
          </div>
          <DialogFooter className="gap-2 pt-1">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function DetailLine({ label, value }) {
  return (
    <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3 border-b border-slate-100 py-2 last:border-0">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <span className="min-w-0 truncate font-semibold text-slate-900">{value || "-"}</span>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono font-semibold text-sm mt-0.5 text-slate-800">{value}</div>
    </div>
  );
}
